/**
 * Convex-backed individual (personal) projects data layer.
 *
 * Mirrors `convex-tasks`: the store and components keep their existing
 * snake_case `IndividualProject` shape, and translation lives here.
 *
 * Projects are single-owner; teams were cut from the product.
 */
import { api } from '@/convex/_generated/api'
import type { Id } from '@/convex/_generated/dataModel'
import { convex, whenConvexReady } from '@/lib/convex-client'
import type {
  IndividualProject,
  IndividualProjectSettings,
  CreateIndividualProjectRequest,
  UpdateIndividualProjectRequest,
} from '@/stores/individual-projects-store'

const DEFAULT_SETTINGS: IndividualProjectSettings = {
  defaultTaskPriority: 'medium',
  autoArchiveCompleted: false,
  showInDashboard: true,
  notificationSettings: { taskReminders: true, deadlineAlerts: true },
  aiSuggestions: true,
  focusMode: false,
}

type ConvexProjectWithStats = {
  _id: Id<'projects'>
  _creationTime: number
  clerkUserId: string
  name: string
  description?: string
  color: string
  status: 'active' | 'completed' | 'archived'
  archivedAt?: number
  settings?: unknown
  taskCount: number
  completedTaskCount: number
  lastActivity: number | null
}

function toStoreProject(doc: ConvexProjectWithStats): IndividualProject {
  const timestamp = new Date(doc._creationTime).toISOString()

  return {
    id: doc._id,
    user_id: doc.clerkUserId,
    name: doc.name,
    description: doc.description ?? null,
    color: doc.color,
    status: doc.status,
    settings: { ...DEFAULT_SETTINGS, ...((doc.settings as Partial<IndividualProjectSettings>) ?? {}) },
    created_at: timestamp,
    updated_at: timestamp,
    taskCount: doc.taskCount,
    completedTaskCount: doc.completedTaskCount,
    lastActivity: doc.lastActivity ? new Date(doc.lastActivity).toISOString() : null,
  }
}

export async function listProjects(includeArchived = false): Promise<IndividualProject[]> {
  await whenConvexReady()
  const docs = await convex.query(api.projects.list, { includeArchived })
  return docs.map((doc) => toStoreProject(doc as ConvexProjectWithStats))
}

export async function getProject(id: string): Promise<IndividualProject | null> {
  await whenConvexReady()
  const doc = await convex.query(api.projects.get, { projectId: id as Id<'projects'> })
  return doc ? toStoreProject(doc as ConvexProjectWithStats) : null
}

export async function createProject(
  data: CreateIndividualProjectRequest,
): Promise<IndividualProject> {
  await whenConvexReady()
  const doc = await convex.mutation(api.projects.create, {
    name: data.name,
    description: data.description,
    color: data.color,
    settings: { ...DEFAULT_SETTINGS, ...(data.settings ?? {}) },
  })

  // create returns the raw document; stats are zero for a new project.
  return toStoreProject({ ...doc, taskCount: 0, completedTaskCount: 0, lastActivity: null } as ConvexProjectWithStats)
}

export async function updateProject(
  id: string,
  data: UpdateIndividualProjectRequest,
): Promise<IndividualProject> {
  await whenConvexReady()
  const doc = await convex.mutation(api.projects.update, {
    projectId: id as Id<'projects'>,
    name: data.name,
    description: data.description,
    color: data.color,
    status: data.status,
    settings: data.settings ? { ...DEFAULT_SETTINGS, ...data.settings } : undefined,
  })

  const refreshed = await getProject(doc._id)
  return refreshed ?? toStoreProject({ ...doc, taskCount: 0, completedTaskCount: 0, lastActivity: null } as ConvexProjectWithStats)
}

export async function setProjectArchived(
  id: string,
  archived: boolean,
): Promise<IndividualProject> {
  await whenConvexReady()
  const doc = await convex.mutation(api.projects.setArchived, {
    projectId: id as Id<'projects'>,
    archived,
  })

  const refreshed = await getProject(doc._id)
  return refreshed ?? toStoreProject({ ...doc, taskCount: 0, completedTaskCount: 0, lastActivity: null } as ConvexProjectWithStats)
}

export async function deleteProject(id: string): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.projects.remove, { projectId: id as Id<'projects'> })
}

/** Live project subscription; mirrors subscribeToTasks. */
export function subscribeToProjects(
  onProjects: (projects: IndividualProject[]) => void,
  options?: { includeArchived?: boolean },
): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void whenConvexReady().then(() => {
    if (cancelled) return
    const watch = convex.watchQuery(api.projects.list, {
      includeArchived: options?.includeArchived ?? false,
    })
    unsubscribe = watch.onUpdate(() => {
      const docs = watch.localQueryResult()
      if (docs) onProjects(docs.map((doc) => toStoreProject(doc as ConvexProjectWithStats)))
    })
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}
